import { api } from '@/lib/api/client';
import type { QueryParams } from '@/lib/api/client';
import type { BlockSchemaRegistry, Page, Paginated, Section } from '@/lib/api/types';

/**
 * Pages + section builder client (spec §9.1). Pages share the publish/CRUD surface with the
 * generic content engine, but sections and the block schema registry are bespoke, so this
 * module is hand-written rather than going through makeResource.
 */
export const pagesApi = {
  async list(params?: QueryParams): Promise<Paginated<Page>> {
    const res = await api.getRaw<Page[]>('/admin/pages', { locale: 'all', ...params });
    return { data: res.data ?? [], meta: res.meta as Paginated<Page>['meta'] };
  },
  get(id: string): Promise<Page> {
    return api.get<Page>(`/admin/pages/${id}`, { locale: 'all' });
  },
  create(body: unknown): Promise<Page> {
    return api.post<Page>('/admin/pages', body);
  },
  update(id: string, body: unknown): Promise<Page> {
    return api.patch<Page>(`/admin/pages/${id}`, body);
  },
  remove(id: string): Promise<null> {
    return api.del(`/admin/pages/${id}`);
  },
  publish(id: string, scheduledAt?: string): Promise<Page> {
    return api.post<Page>(
      `/admin/pages/${id}/publish`,
      scheduledAt ? { scheduled_at: scheduledAt } : undefined,
    );
  },
  unpublish(id: string): Promise<Page> {
    return api.post<Page>(`/admin/pages/${id}/unpublish`);
  },

  // ─── Section builder ─────────────────────────────────────────────────────
  blockSchemas(): Promise<BlockSchemaRegistry> {
    return api.get<BlockSchemaRegistry>('/admin/blocks/schemas');
  },
  listSections(pageId: string): Promise<Section[]> {
    return api.get<Section[]>(`/admin/pages/${pageId}/sections`);
  },
  addSection(
    pageId: string,
    body: {
      type: string;
      anchor?: string;
      is_visible?: boolean;
      sort_order?: number;
      data: Record<string, unknown>;
    },
  ): Promise<Section> {
    return api.post<Section>(`/admin/pages/${pageId}/sections`, body);
  },
  updateSection(sectionId: string, body: unknown): Promise<Section> {
    return api.patch<Section>(`/admin/sections/${sectionId}`, body);
  },
  deleteSection(sectionId: string): Promise<null> {
    return api.del(`/admin/sections/${sectionId}`);
  },
  reorderSections(pageId: string, orderedIds: string[]): Promise<null> {
    return api.post(`/admin/pages/${pageId}/sections/reorder`, { ordered_ids: orderedIds });
  },
};
